import * as React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { NotificationSlice } from 'notification/exports';
import { history } from '../../history/history';

import { AccountsServiceModels } from 'accounts/exports';
import { BillingResources } from 'billing/exports';

import noPages from './images/nopages.svg';

import { EmptyPage } from './EmptyPage';

import { IPlanFeatures, IPlanFeatureLimit } from '../../../../billing/src/model';
import { INotificationAction } from '../../../../notif/src/model';
import { ICustomSetting } from '../../../../accounts/src/model';
import { MobileEmptyPage } from './mobile/MobileCommons';
import IStoreState from '../../../../main/src/store/model';
import { isSendUser } from '../../services/common/apiUtils';
const { actions: NotificationActions } = NotificationSlice;
const { BillingRoutes } = BillingResources;

export interface IPermissionsAndLimitsContext {
	permissions: AccountsServiceModels.PermissionsEnum[];
	limits: IPlanFeatures;
	userSettings?: ICustomSetting[];
}
const PermissionsAndLimitsContext = React.createContext<IPermissionsAndLimitsContext>({ permissions: [], limits: {}, userSettings: [] });

export const usePermissionsAndLimitsContext = () => React.useContext(PermissionsAndLimitsContext);

export const PermissionsAndLimitsContextProvider = PermissionsAndLimitsContext.Provider;

/**
 * Permissions Check Component
 */
export interface IPermissionsCheckProps {
	requiredPermissions: AccountsServiceModels.PermissionsEnum[];
	mode?: 'no-render' | 'fallback' | 'always-render';
	fallbackElement?: React.ReactElement;
	children: any;
}

export const PermissionsCheck: React.FC<IPermissionsCheckProps> = (props) => {
	const context = usePermissionsAndLimitsContext();
	const pass = props.requiredPermissions.every((item) => context.permissions.includes(item));

	const renderChildren = () => {
		return typeof props.children === 'function' ? props.children(context, pass) : props.children;
	};

	if (props.mode === 'always-render') {
		return <>{renderChildren()}</>;
	}

	if (!pass && props.mode === 'fallback') {
		return <>{props.fallbackElement}</>;
	}

	if (!pass && props.mode === 'no-render') {
		return null;
	}

	return <>{renderChildren()}</>;
};

PermissionsCheck.defaultProps = {
	mode: 'no-render',
};

PermissionsCheck.displayName = 'PermissionsCheck';

/**
 * Limits Check Component
 */
export interface ILimitsCheckProps {
	currentValue: {
		code: string | string[];
		value: number;
		plan?: string;
	};
	mode?: 'no-render' | 'fallback' | 'always-render';
	fallbackElement?: React.ReactElement;
	children: any;
}

const shouldPass = (featureLimit: IPlanFeatureLimit, value: number) =>
	!featureLimit || (featureLimit.Limit ? value <= featureLimit.Limit : false);

export const LimitsCheck: React.FC<ILimitsCheckProps> = (props) => {
	const context = usePermissionsAndLimitsContext();

	const { currentValue } = props;

	let returnFeature: IPlanFeatureLimit;
	let pass = true;

	if (Array.isArray(currentValue.code)) {
		currentValue.code.forEach((item) => {
			if (pass) {
				const featureLimit = context.limits[item];

				pass = shouldPass(featureLimit, currentValue.value);
				if (!pass) {
					returnFeature = featureLimit;
				}
			}
		});
	} else {
		const featureLimit = context.limits[currentValue.code];

		pass = shouldPass(featureLimit, currentValue.value);
		returnFeature = featureLimit;
	}

	const dispatch = useDispatch();

	const upgradeNotification = () => {
		if (currentValue.code === 'transactional.api' && currentValue.value === 0 && isSendUser()) {
			dispatch(
				NotificationActions.showNotification({
					message: 'To access this feature, please contact our Sales or Support Team for assistance.',
					type: 'info',
					notificationDuration: 10000,
				}),
			);
		} else {
			dispatch(
				NotificationActions.showNotification({
					message: 'Upgrade plan to use this feature.',
					type: 'info',
					onClickOk: () => history.push(`${BillingRoutes.plans}${currentValue.plan ? `?plan=${currentValue.plan}` : ''}`),
					okButtonLabel: 'Upgrade',
					notificationDuration: 10000,
				}),
			);
		}
	};

	const renderChildren = () =>
		typeof props.children === 'function' ? props.children(context, pass, returnFeature, upgradeNotification) : props.children;

	if (props.mode === 'always-render') {
		return <>{renderChildren()}</>;
	}

	if (!pass && props.mode === 'fallback') {
		return <>{props.fallbackElement}</>;
	}

	if (!pass && props.mode === 'no-render') {
		return null;
	}

	return <>{renderChildren()}</>;
};

LimitsCheck.defaultProps = {
	mode: 'no-render',
};

LimitsCheck.displayName = 'LimitsCheck';

export const withLimitNotification =
	<P extends { onClick?: (...args: any[]) => void; children?: any }>(
		Cmp: React.ComponentType<P>,
		currentValue: ILimitsCheckProps['currentValue']
	) =>
	(prps: P) => {
		return (
			<LimitsCheck currentValue={currentValue} mode="always-render">
				{(ctx: IPermissionsAndLimitsContext, pass: boolean, featureLimit: any, upgradeNotification: () => void) => {
					if (featureLimit && typeof currentValue.value !== 'undefined' && featureLimit.Limit <= currentValue.value) {
						if ((Cmp as any) === Link) {
							if (Array.isArray(prps.children)) {
								return React.Children.map(prps.children, (item) => {
									return React.cloneElement(item, { onClick: upgradeNotification } as any);
								});
							} else if (React.isValidElement(prps.children)) {
								return React.cloneElement(prps.children, { onClick: upgradeNotification } as any);
							}
						}

						return (
							<Cmp {...prps} onClick={upgradeNotification}>
								{prps.children}
							</Cmp>
						);
					}
					return <Cmp {...prps}>{prps.children}</Cmp>;
				}}
			</LimitsCheck>
		);
	};

export const withLimitNotificationUpgrade =
	<P extends { onClick?: (...args: any[]) => void; children?: any }>(
		Cmp: React.ComponentType<P>,
		currentValue: ILimitsCheckProps['currentValue']
	) =>
	(prps: P) => {
		const context = usePermissionsAndLimitsContext();
		const dispatch = useDispatch();
		const limits = useSelector((state: IStoreState) => state.billing.currentPlans.Features);
		const featureLimit = limits?.[currentValue.code] || context.limits[currentValue.code];
		return (
			<LimitsCheck currentValue={currentValue} mode="always-render">
				{(ctx: IPermissionsAndLimitsContext, pass: boolean, featureLimit: any, upgradeNotification: () => void) => {
					const limits = useSelector((state: IStoreState) => state.billing.currentPlans.Features);
					const featureLimitFromChargebee = limits?.[currentValue.code] || context.limits[currentValue.code];

					if (
						featureLimitFromChargebee &&
						typeof currentValue.value !== 'undefined' &&
						featureLimitFromChargebee.Limit <= currentValue.value
					) {
						if ((Cmp as any) === Link) {
							if (Array.isArray(prps.children)) {
								return React.Children.map(prps.children, (item) => {
									return React.cloneElement(item, { onClick: upgradeNotification } as any);
								});
							} else if (React.isValidElement(prps.children)) {
								return React.cloneElement(prps.children, { onClick: upgradeNotification } as any);
							}
						}

						return (
							<Cmp {...prps} onClick={upgradeNotification}>
								{prps.children}
							</Cmp>
						);
					}
					return <Cmp {...prps}>{prps.children}</Cmp>;
				}}
			</LimitsCheck>
		);
	};

export const LimitFallbackPage: React.FC<{ currentValue: ILimitsCheckProps['currentValue']; title?: string; image?: string }> = (props) => {
	return (
		<LimitsCheck
			currentValue={props.currentValue}
			mode="fallback"
			fallbackElement={
				<>
					<EmptyPage
						title={props.title || 'Upgrade to continue using this feature'}
						route={BillingRoutes.plans}
						src={props.image || noPages}
						style={{ padding: '150px 0px 30px' }}
						label="Upgrade"
					/>
					<MobileEmptyPage
						title={props.title || 'Upgrade to continue using this feature from Billing section'}
						src={props.image || noPages}
					/>
				</>
			}
		>
			{props.children}
		</LimitsCheck>
	);
};

interface IUserSettingsCheck {
	currentValue: {
		name: string;
		value: string;
	};
	mode?: 'no-render' | 'fallback' | 'always-render';
	fallbackElement?: React.ReactElement;
	children: any;
}

export const UserSettingsCheck: React.FC<IUserSettingsCheck> = (props) => {
	const context = usePermissionsAndLimitsContext();

	const { currentValue } = props;

	const feature = context.userSettings?.find((item) => item.Property === currentValue.name);

	const pass = !feature || feature.Value !== 'true';

	const renderChildren = () => {
		return typeof props.children === 'function' ? props.children(context, pass, feature) : props.children;
	};

	if (props.mode === 'always-render') {
		return <>{renderChildren()}</>;
	}

	if (!pass && props.mode === 'fallback') {
		return <>{props.fallbackElement}</>;
	}

	if (!pass && props.mode === 'no-render') {
		return null;
	}

	return <>{renderChildren()}</>;
};

UserSettingsCheck.defaultProps = {
	mode: 'no-render',
};

UserSettingsCheck.displayName = 'UserSettingsCheck';

export const useLimitCheck = () => {
	const context = usePermissionsAndLimitsContext();
	const dispatch = useDispatch();

	// Will execute callback if limit is not met, third param is optional, in case this function is used outside of context
	const checkLimit = (
		cb: () => void,
		currentValue: ILimitsCheckProps['currentValue'],
		limits?: IPlanFeatures,
		notificationProps?: Partial<INotificationAction>
	) => {
		const featureLimit = limits?.[currentValue.code] || context.limits[currentValue.code];
		if (featureLimit && typeof currentValue.value !== 'undefined' && featureLimit.Limit <= currentValue.value) {
			dispatch(
				NotificationActions.showNotification({
					message: 'Upgrade plan to use this feature.',
					type: 'info',
					onClickOk: () => history.push(`${BillingRoutes.plans}${currentValue.plan ? `?plan=${currentValue.plan}` : ''}`),
					okButtonLabel: 'Upgrade',
					notificationDuration: 10000,
					...notificationProps,
				})
			);
		} else {
			return cb();
		}
	};

	return {
		checkLimit,
	};
};

export default PermissionsAndLimitsContext;
